import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
  Chip,
  Stack,
  Autocomplete,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { bookingsAPI, contactsAPI, slotsAPI, subCategoriesAPI, googleAuthAPI, guestsAPI } from '../services/api';
import { toast } from 'react-toastify';

const DEFAULT_NEW_BOOKING_STATUS = 'pending';

const toLocalIsoDate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** GET /customer/generate-slots/user-slots — only `isAvailable: true` slots are bookable. */
function isSlotBookableFromUserSlotsApi(slot) {
  if (!slot) return false;
  if (slot.isVirtual) return true;
  const avail = slot.isAvailable ?? slot.is_available;
  if (avail === true || avail === 1) return true;
  const s = String(avail ?? '').trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

/** Normalize GET /google-auth/status body — true when calendar OAuth completed / refresh token stored. */
function parseGoogleCalendarLinkedFromStatusResponse(resData) {
  const root = resData?.data !== undefined ? resData.data : resData;
  const o =
    root && typeof root === 'object' && root.data !== undefined && typeof root.data === 'object' && !Array.isArray(root.data)
      ? root.data
      : root;
  if (!o || typeof o !== 'object') return false;
  const str = (v) => (v === undefined || v === null ? '' : String(v).toUpperCase());
  const truthy = (v) => v === true || v === 1 || str(v) === 'TRUE' || str(v) === 'YES';
  const keys = [
    'isConnected',
    'connected',
    'isGoogleCalendarConnected',
    'hasRefreshToken',
    'isLinked',
    'googleCalendarLinked',
    'calendarLinked',
    'oauthCompleted',
    'hasGoogleCalendar',
  ];
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(o, k) && truthy(o[k])) return true;
  }
  return false;
}

/** User-facing text from booking create/update API (`error.message`, `error.details`, etc.). */
function getBookingApiUserMessage(error) {
  const d = error?.response?.data;
  if (!d) return error?.message ? String(error.message) : '';
  const nested = d.error;
  if (nested && typeof nested === 'object') {
    const msg = nested.message != null ? String(nested.message).trim() : '';
    const det = nested.details != null ? String(nested.details).trim() : '';
    if (msg && det && det !== msg) return `${msg}\n${det}`;
    return msg || det;
  }
  if (typeof d.error === 'string') return String(d.error).trim();
  return String(d.message || d.errorMessage || d.details || '').trim();
}

function getServiceCategoryIdFromContact(contact) {
  if (!contact || typeof contact !== 'object') return null;
  const id =
    contact.serviceCategoryId ??
    contact.categoryId ??
    contact.contactTypeId ??
    contact.contactCategoryId;
  if (id !== undefined && id !== null && id !== '') return id;
  return null;
}

/** GET booking may nest `{ booking: { serviceSubCategoryIds: "7,24" } }` — flatten for reads. */
function mergeBookingPayloadForForm(p) {
  if (!p || typeof p !== 'object') return p;
  const nested =
    (p.booking && typeof p.booking === 'object' && p.booking) ||
    (p.bookingDetails && typeof p.bookingDetails === 'object' && p.bookingDetails) ||
    null;
  if (!nested) return p;
  // Inner booking is canonical; spread parent then nested again so wrapper keys never wipe e.g. `customerFirstName`.
  return { ...nested, ...p, ...nested };
}

/** First non-empty trimmed value. GET booking often has both `customerFirstName` and `guest.firstName`; prefer customer* for edit. */
function pickNonEmptyStr(...candidates) {
  for (const c of candidates) {
    if (c === undefined || c === null) continue;
    const s = String(c).trim();
    if (s) return s;
  }
  return '';
}

const sanitizePhoneInput = (val) => {
  if (!val || typeof val !== 'string') return '';
  return val.replace(/[^0-9+]/g, '').replace(/(?!^)\+/g, '').slice(0, 20);
};

const isValidEmail = (val) => {
  if (!val || typeof val !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(val.trim());
};

const isValidPhoneNumber = (val) => {
  if (!val || typeof val !== 'string') return false;
  const trimmed = val.trim();
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length < 6 || digitsOnly.length > 15) return false;
  return /^\+?[0-9]{6,15}$/.test(trimmed);
};

/** Map API gender variants onto Select values `male` | `female` | `other`. */
function normalizeBookingGender(raw) {
  const g = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (['male', 'female', 'other'].includes(g)) return g;
  if (g === 'm' || g === 'man' || g === '1') return 'male';
  if (g === 'f' || g === 'woman' || g === '2') return 'female';
  return '';
}

function mergedLayerLooksLikeBooking(m) {
  if (!m || typeof m !== 'object') return false;
  if (m.id != null || m.bookingId != null) return true;
  if (String(m.title ?? '').trim()) return true;
  if (
    pickNonEmptyStr(
      m.customerFirstName,
      m.CustomerFirstName,
      m.firstName,
      m.customerName,
      m.name
    )
  ) {
    return true;
  }
  const g = m.guest;
  if (g && typeof g === 'object' && !Array.isArray(g)) {
    if (pickNonEmptyStr(g.firstName, g.customerFirstName, g.email)) return true;
  }
  return false;
}

/** Normalize GET /customer/bookings/:id response (several axios / API envelope shapes). */
function unwrapGetBookingDetail(res) {
  const body = res?.data;
  if (!body || typeof body !== 'object') return null;
  const layers = [
    body?.data?.data?.data,
    body?.data?.data,
    body?.data,
    body?.payload?.data,
    body?.payload,
    body,
  ];
  for (const layer of layers) {
    if (!layer || typeof layer !== 'object' || Array.isArray(layer)) continue;
    const m = mergeBookingPayloadForForm(layer);
    if (mergedLayerLooksLikeBooking(m)) return m;
  }
  for (const layer of layers) {
    if (layer && typeof layer === 'object' && !Array.isArray(layer)) {
      return mergeBookingPayloadForForm(layer);
    }
  }
  return null;
}

function parseIdsListField(raw) {
  if (raw == null || raw === '') return [];
  const parts = Array.isArray(raw) ? raw : String(raw).split(',');
  return parts
    .map((x) => Number.parseInt(String(x).trim(), 10))
    .filter((n) => !Number.isNaN(n) && n > 0);
}

function parseRawSubCategoryIdList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map(Number).filter((n) => !Number.isNaN(n));
  }
  return String(raw)
    .split(',')
    .map((x) => Number.parseInt(String(x).trim(), 10))
    .filter((n) => !Number.isNaN(n));
}

function parseSubCategoryIdsFromMixedArray(subs) {
  if (!Array.isArray(subs) || subs.length === 0) return [];
  if (subs.every((x) => typeof x === 'string')) return [];
  return subs
    .map((x) => {
      if (x && typeof x === 'object') {
        const id = Number(x.id ?? x.subCategoryId);
        return Number.isFinite(id) ? id : Number.NaN;
      }
      const s = String(x).trim().replace(/^#/, '');
      const n = Number.parseInt(s, 10);
      return Number.isFinite(n) ? n : Number.NaN;
    })
    .filter((n) => !Number.isNaN(n));
}

/** Saved booking uses ids in multi-select — contact list may expose labels only; then load API list. */
function hasNumericIdSelection(sel) {
  if (!Array.isArray(sel) || sel.length === 0) return false;
  return sel.every((v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  });
}

/** Ids from a contact row returned by GET /customer/contacts (same shapes as Contacts page) */
function parseSubCategoryIdsFromContactRow(contact) {
  if (!contact || typeof contact !== 'object') return [];
  const merged = [
    ...new Set([
      ...parseIdsListField(contact.serviceSubCategoryIds),
      ...parseIdsListField(contact.subCategoryIds),
    ]),
  ];
  if (merged.length > 0) return merged;
  const ids = parseRawSubCategoryIdList(contact.subCategoryId);
  if (ids.length > 0) return ids;
  return parseSubCategoryIdsFromMixedArray(contact.subCategories);
}

/** Display names parallel to `subCategoryIds` on GET /customer/contacts (names or string[] subCategories). */
function getContactSubcategoryDisplayNames(contact) {
  if (!contact || typeof contact !== 'object') return [];
  if (Array.isArray(contact.subCategoryNames) && contact.subCategoryNames.length > 0) {
    return contact.subCategoryNames.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof contact.subCategoryNames === 'string' && contact.subCategoryNames.trim()) {
    return contact.subCategoryNames.split(',').map((s) => s.trim()).filter(Boolean);
  }
  if (
    Array.isArray(contact.subCategories) &&
    contact.subCategories.length > 0 &&
    contact.subCategories.every((x) => typeof x === 'string')
  ) {
    return contact.subCategories.map((s) => String(s).trim()).filter(Boolean);
  }
  return [];
}

/** Display label for a subcategory row (contact or GET /subcategories). Supports `{ id, type }` from API. */
function getSubcategoryRowLabel(s, id) {
  if (!s || typeof s !== 'object') return `Subcategory ${id}`;
  const raw =
    s.name_EN ??
    s.name ??
    s.title_EN ??
    s.title ??
    s.type ??
    s.subType ??
    s.label ??
    s.title_DE;
  if (raw != null && String(raw).trim()) return String(raw).trim();
  return `Subcategory ${id}`;
}

/**
 * Prefer subcategory rows already present on the contact object from GET /customer/contacts.
 * Returns [{ value, label, kind: 'id'|'name' }, ...] for the multi-select.
 * When the API sends `subCategoryIds` + names (`subCategoryNames` or string `subCategories`), options use ids so POST matches GET.
 */
function buildSubcategoryOptionsFromContactListRow(contact) {
  if (!contact || typeof contact !== 'object') return [];

  const objectArrays = [
    contact.subCategoryDetails,
    contact.subCategoriesList,
    contact.serviceSubCategories,
    Array.isArray(contact.subCategories) && contact.subCategories.length && typeof contact.subCategories[0] === 'object'
      ? contact.subCategories
      : null,
  ].filter(Boolean);

  for (const arr of objectArrays) {
    if (!Array.isArray(arr)) continue;
    const rows = arr
      .map((s) => {
        if (!s || typeof s !== 'object') return null;
        const id = Number(s.id ?? s.subCategoryId ?? s.SubCategoryId);
        if (!Number.isFinite(id) || id <= 0) return null;
        const label = getSubcategoryRowLabel(s, id);
        return { value: id, label, kind: 'id' };
      })
      .filter(Boolean);
    if (rows.length > 0) return rows;
  }

  const ids = parseSubCategoryIdsFromContactRow(contact);
  if (ids.length > 0) {
    const names = getContactSubcategoryDisplayNames(contact);
    return ids.map((id, i) => ({
      value: id,
      label: names[i] || `Subcategory ${id}`,
      kind: 'id',
    }));
  }

  // No ids: string[] subCategories only — value is label (resolve to ids on submit if possible)
  if (Array.isArray(contact.subCategories) && contact.subCategories.length > 0) {
    const allStrings = contact.subCategories.every((x) => typeof x === 'string');
    if (allStrings) {
      return contact.subCategories
        .map((s) => String(s).trim())
        .filter(Boolean)
        .map((name) => ({ value: name, label: name, kind: 'name' }));
    }
  }

  return [];
}

function parseSubCategoriesListResponse(res) {
  const body = res?.data;
  const list =
    body?.payload ??
    body?.data?.data ??
    body?.data ??
    (Array.isArray(body) ? body : []);
  return Array.isArray(list) ? list : [];
}

/** Match a display label to a subcategory row from GET subcategories (for name-only contact lists). */
function matchSubcategoryIdByLabel(apiList, label) {
  if (label == null || !Array.isArray(apiList)) return null;
  const target = String(label).trim().toLowerCase();
  if (!target) return null;
  for (const s of apiList) {
    if (!s || typeof s !== 'object') continue;
    const id = Number(s.id ?? s.subCategoryId);
    if (!Number.isFinite(id) || id <= 0) continue;
    const candidates = [s.name_EN, s.name, s.title_EN, s.title, s.type, s.subType, s.label]
      .filter((x) => x != null && String(x).trim())
      .map((x) => String(x).trim().toLowerCase());
    if (candidates.includes(target)) return id;
  }
  return null;
}

/** Values for multi-select: numeric ids and/or string labels (matches contact `subCategories` name list). */
function parseSubCategorySelectionFromBooking(booking) {
  if (!booking || typeof booking !== 'object') return [];
  const mergedIds = [
    ...new Set([
      ...parseIdsListField(booking.serviceSubCategoryIds ?? booking.service_sub_category_ids),
      ...parseIdsListField(booking.subCategoryIds),
    ]),
  ];
  if (mergedIds.length > 0) return mergedIds;
  const ids = parseRawSubCategoryIdList(booking.subCategoryId);
  if (ids.length > 0) return ids;
  const subs = booking.subCategories;
  if (Array.isArray(subs) && subs.length > 0) {
    if (subs.every((x) => typeof x === 'string')) {
      return subs.map((s) => String(s).trim()).filter(Boolean);
    }
    return parseSubCategoryIdsFromMixedArray(subs);
  }
  if (Array.isArray(booking.subCategoryNames) && booking.subCategoryNames.length) {
    return booking.subCategoryNames.map(String).filter(Boolean);
  }
  return [];
}

function splitSubCategoryPayload(selection, options) {
  const ids = [];
  const names = [];
  const sel = Array.isArray(selection) ? selection : [];
  for (const v of sel) {
    const opt = options.find(
      (o) =>
        o.value === v ||
        (typeof o.value === 'number' && typeof v === 'number' && o.value === v) ||
        String(o.value) === String(v)
    );
    if (opt?.kind === 'name') {
      names.push(String(v));
    } else if (opt?.kind === 'id' && Number.isFinite(Number(v))) {
      ids.push(Number(v));
    } else if (typeof v === 'string' && !/^\d+$/.test(String(v).trim())) {
      names.push(String(v));
    } else {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) ids.push(n);
    }
  }
  return { ids, names };
}

function selectionIncludesValue(selection, value) {
  const sel = Array.isArray(selection) ? selection : [];
  return sel.some((s) => s === value || (typeof s === 'number' && typeof value === 'number' && s === value) || String(s) === String(value));
}

function mapSubCategoriesApiListToOptions(list) {
  return list
    .map((s) => {
      const id = Number(s.id ?? s.subCategoryId);
      if (!Number.isFinite(id) || id <= 0) return null;
      const label = getSubcategoryRowLabel(s, id);
      return { value: id, label, kind: 'id' };
    })
    .filter(Boolean);
}

function resolveSubcategoryOptionsAfterFetch(rows, nameOnly, fromList) {
  if (rows.length > 0) return rows;
  if (nameOnly) return fromList;
  return [];
}

function normalizeSubCategorySelectValue(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',');
  return [];
}

function getBookingFormDialogTitle(booking, createStep, translate) {
  if (booking) return translate('bookingForm.titleUpdate');
  if (createStep === 0) return 'Add Booking Details';
  return 'Select Date & Slot';
}

function getBookingFormPrimaryActionLabel(isCreateFlow, createStep, booking, translate) {
  if (isCreateFlow && createStep === 0) return 'Next';
  if (booking) return translate('bookingForm.titleUpdate');
  return translate('bookingForm.titleCreate');
}

function formatSelectedDateLongLabel(selectedDate) {
  const [y, mo, d] = selectedDate.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function buildDisplayedContactSlots(contactSlots, booking, formSlotId) {
  const list = Array.isArray(contactSlots) ? contactSlots : [];
  if (!booking || !formSlotId) return list;
  const exists = list.some((s) => String(s.id) === String(formSlotId));
  if (exists) return list;

  const st =
    booking.slotTime ??
    booking.startTime ??
    booking.appointmentStartTime ??
    booking.slotStartTime ??
    booking.start_time ??
    booking.slot?.slotTime ??
    booking.slot?.startTime ??
    booking.slot?.start_time;
  const et =
    booking.slotEndTime ??
    booking.endTime ??
    booking.appointmentEndTime ??
    booking.slotEndTime ??
    booking.end_time ??
    booking.slot?.slotEndTime ??
    booking.slot?.endTime ??
    booking.slot?.end_time;

  if (st && et) {
    return [
      {
        id: formSlotId,
        slotTime: st,
        slotEndTime: et,
        date: booking.date || booking.slotDate,
        isVirtual: true,
      },
      ...list,
    ];
  }
  return list;
}

function getSlotTimeLabel(slot, translate) {
  const st = slot.slotTime ?? slot.startTime ?? slot.slotStartTime ?? slot.start_time;
  const et = slot.slotEndTime ?? slot.endTime ?? slot.slotEndTime ?? slot.end_time;
  if (st && et) {
    return `${String(st).substring(0, 5)} – ${String(et).substring(0, 5)}`;
  }
  return translate('bookingForm.slotLabel', { id: String(slot.id).substring(0, 8) });
}

function getSlotChipTextColor(isSelected, disabled) {
  if (isSelected) return '#fff';
  if (disabled) return 'text.disabled';
  return 'text.primary';
}

function getSlotChipHoverBorderColor(disabled, isSelected) {
  if (disabled) return isSelected ? '#FF8C42' : '#e0e0e0';
  return '#FF8C42';
}

function resolveMeetingType(rawMeetingType) {
  return String(rawMeetingType).toLowerCase() === 'online' ? 'online' : 'offline';
}

function resolveBookingSelectedDate(bookingRecord, todayIso) {
  if (bookingRecord?.date) return String(bookingRecord.date).split('T')[0];
  if (bookingRecord?.slotDate) return String(bookingRecord.slotDate).split('T')[0];
  return todayIso;
}

function buildFormStateFromBooking(bookingRecord) {
  const guest =
    bookingRecord.guest && typeof bookingRecord.guest === 'object' && !Array.isArray(bookingRecord.guest)
      ? bookingRecord.guest
      : {};
  const rawMeetingType =
    bookingRecord.meetingType ??
    bookingRecord.meeting_type ??
    bookingRecord.MeetingType ??
    bookingRecord.bookingType ??
    bookingRecord.booking_type ??
    bookingRecord.mode ??
    'offline';

  return {
    title: bookingRecord.title || '',
    contactId: bookingRecord.contactId || '',
    subCategorySelection: parseSubCategorySelectionFromBooking(bookingRecord),
    firstName: pickNonEmptyStr(
      bookingRecord.customerFirstName,
      bookingRecord.CustomerFirstName,
      bookingRecord.customer_first_name,
      bookingRecord.firstName,
      bookingRecord.FirstName,
      bookingRecord.givenName,
      guest.customerFirstName,
      guest.firstName,
      guest.FirstName,
      bookingRecord.customerName,
      bookingRecord.CustomerName
    ),
    lastName: pickNonEmptyStr(
      bookingRecord.customerLastName,
      bookingRecord.CustomerLastName,
      bookingRecord.customer_last_name,
      bookingRecord.lastName,
      bookingRecord.LastName,
      bookingRecord.familyName,
      guest.customerLastName,
      guest.lastName,
      guest.LastName
    ),
    email: pickNonEmptyStr(
      bookingRecord.customerEmail,
      bookingRecord.CustomerEmail,
      bookingRecord.customer_email,
      guest.email,
      guest.Email
    ),
    phone: pickNonEmptyStr(
      bookingRecord.customerPhone,
      bookingRecord.CustomerPhone,
      bookingRecord.customer_phone,
      guest.phone,
      guest.Phone
    ),
    gender: normalizeBookingGender(
      bookingRecord.customerGender ??
        guest.gender ??
        bookingRecord.gender ??
        bookingRecord.Gender ??
        guest.Gender
    ),
    slotId: bookingRecord.slotId || '',
    status: String(bookingRecord.status || 'APPROVE').toUpperCase(),
    serviceAddress: bookingRecord.serviceAddress || '',
    notes: bookingRecord.notes || '',
    numberOfGuests: bookingRecord.numberOfGuests ?? 1,
    meetingType: resolveMeetingType(rawMeetingType),
  };
}

async function fetchBookingDetailRecord(booking) {
  let bookingRecord = mergeBookingPayloadForForm({ ...booking });
  const bookingId = booking.id ?? booking.bookingId;
  if (bookingId) {
    try {
      const res = await bookingsAPI.getBooking(bookingId);
      const detail = unwrapGetBookingDetail(res);
      if (detail && typeof detail === 'object') {
        bookingRecord = { ...bookingRecord, ...detail };
      }
    } catch (error) {
      console.warn('BookingForm: getBooking failed', error);
    }
  }
  return mergeBookingPayloadForForm(bookingRecord);
}

const initialForm = {
  title: '',
  contactId: '',
  subCategorySelection: [],
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  gender: '',
  slotId: '',
  status: 'APPROVE',
  serviceAddress: '',
  notes: '',
  numberOfGuests: 1,
  meetingType: 'offline',
};

// ─── Mini Calendar Component ────────────────────────────────────────────────
function getMiniCalendarDayFontWeight(selected, today) {
  if (selected) return 800;
  if (today) return 700;
  return 500;
}

function getMiniCalendarDayColor(disabled, selected, today) {
  if (disabled) return '#ccc';
  if (selected) return '#fff';
  if (today) return '#FF8C42';
  return '#333';
}

function getMiniCalendarTodayBorder(today, selected) {
  if (!today) return 'none';
  if (selected) return 'none';
  return '2px solid #FF8C42';
}

function MiniCalendarDay({ day, disabled, selected, today, onSelect }) {
  return (
    <Box
      onClick={() => onSelect(day)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 38,
        borderRadius: '50%',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: getMiniCalendarDayFontWeight(selected, today),
        fontSize: '0.875rem',
        color: getMiniCalendarDayColor(disabled, selected, today),
        background: selected ? '#FF8C42' : 'transparent',
        border: getMiniCalendarTodayBorder(today, selected),
        boxShadow: 'none',
        transition: 'all 0.15s',
        '&:hover': disabled
          ? {}
          : {
              background: selected ? '#FF8C42' : 'rgba(255, 140, 66, 0.08)',
              transform: 'scale(1.08)',
            },
      }}
    >
      {day}
    </Box>
  );
}

function MiniCalendar({ value, originalValue, minDate, onChange, onClose }) {
  const today = new Date();
  const initDate = value ? new Date(value + 'T00:00:00') : today;
  const [viewYear, setViewYear] = useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initDate.getMonth());

  const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : null;

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const isDisabled = (day) => {
    const d = new Date(viewYear, viewMonth, day);
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const todayStr = toLocalIsoDate();

    // Disable if it's in the past AND not the original value
    if (iso < todayStr && iso !== originalValue) return true;

    // Also respect minDate if provided
    if (minDateObj) {
      return d < new Date(minDateObj.getFullYear(), minDateObj.getMonth(), minDateObj.getDate());
    }
    return false;
  };

  const isSelected = (day) => {
    if (!value) return false;
    const [y, mo, d] = value.split('-').map(Number);
    return y === viewYear && (mo - 1) === viewMonth && d === day;
  };

  const isToday = (day) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === day;

  const handleSelect = (day) => {
    if (isDisabled(day)) return;
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    // Even if not "disabled" by minDate, we might want to check if it's the current selection in the past
    onChange(iso);
  };

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box
          component="button"
          onClick={prevMonth}
          sx={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#666', px: 1, borderRadius: 1, '&:hover': { bgcolor: '#f0f0f0' } }}
        >&#8249;</Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>{monthName}</Typography>
        <Box
          component="button"
          onClick={nextMonth}
          sx={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 20, color: '#666', px: 1, borderRadius: 1, '&:hover': { bgcolor: '#f0f0f0' } }}
        >&#8250;</Box>
      </Box>

      {/* Weekday headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
        {weekDays.map(w => (
          <Typography key={w} sx={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {w}
          </Typography>
        ))}
      </Box>

      {/* Day grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map((day, idx) => (
          <Box key={idx}>
            {day ? (
              <MiniCalendarDay
                day={day}
                disabled={isDisabled(day)}
                selected={isSelected(day)}
                today={isToday(day)}
                onSelect={handleSelect}
              />
            ) : (
              <Box />
            )}
          </Box>
        ))}
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            border: '1.5px solid #e0e0e0', background: '#fff', color: '#666',
            borderRadius: 2, px: 2.5, py: 0.8, fontWeight: 600, fontSize: '0.8rem',
            cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' },
          }}
        >Close</Box>
      </Box>
    </Box>
  );
}

function AvailableSlotsPanel({
  contactId,
  slotsLoading,
  contactSlots,
  booking,
  formSlotId,
  translate,
  onSelectSlot,
}) {
  if (!contactId) {
    return <Typography variant="body2" color="text.secondary">{translate('bookingForm.selectServiceFirst')}</Typography>;
  }
  if (slotsLoading) {
    return <Typography variant="body2" color="text.secondary">Loading slots…</Typography>;
  }

  const displayedSlots = buildDisplayedContactSlots(contactSlots, booking, formSlotId);
  if (displayedSlots.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: 'center', bgcolor: '#fafafa', borderRadius: 2, border: '1.5px dashed #e0e0e0' }}>
        <Typography variant="body2" color="text.secondary">No slots available for selected date</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {displayedSlots.map((s) => {
        const timeStr = getSlotTimeLabel(s, translate);
        const isSelected = String(formSlotId) === String(s.id);
        const isCurrentlySelectedSlot =
          String(booking?.slotId) === String(s.id) ||
          (booking && String(formSlotId) === String(s.id));
        const bookable = isSlotBookableFromUserSlotsApi(s);
        const disabled = isCurrentlySelectedSlot ? false : !bookable;

        return (
          <Box
            key={s.id}
            onClick={() => {
              if (disabled) return;
              onSelectSlot(s.id);
            }}
            sx={{
              px: 2.5,
              py: 1.2,
              borderRadius: 2.5,
              cursor: disabled ? 'not-allowed' : 'pointer',
              border: isSelected ? '2px solid #FF8C42' : '1.5px solid #e0e0e0',
              background: isSelected ? '#FF8C42' : '#f9fafb',
              color: getSlotChipTextColor(isSelected, disabled),
              opacity: isSelected ? 1 : disabled ? 0.6 : 1,
              fontWeight: 700,
              fontSize: '0.875rem',
              boxShadow: 'none',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: getSlotChipHoverBorderColor(disabled, isSelected),
                transform: disabled ? 'none' : 'translateY(-1px)',
                boxShadow: 'none',
              },
            }}
          >
            {timeStr}
          </Box>
        );
      })}
    </Box>
  );
}

async function fetchSubCategoryOptionsForCategory(catId, nameOnly, fromList) {
  const res = await subCategoriesAPI.getSubCategories(catId);
  const list = parseSubCategoriesListResponse(res);
  const rows = mapSubCategoriesApiListToOptions(list);
  return { rows, options: resolveSubcategoryOptionsAfterFetch(rows, nameOnly, fromList) };
}

function shouldFetchSubCategoryIdOptions(fromList, subCategorySelection, catId) {
  const nameOnly = fromList.length > 0 && fromList.every((o) => o.kind === 'name');
  return Boolean(catId) && (fromList.length === 0 || (nameOnly && hasNumericIdSelection(subCategorySelection)));
}

// ─── Main BookingForm Component ──────────────────────────────────────────────
export default function BookingForm({ open, onClose, onSuccess, booking = null }) {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(initialForm);
  const [contactSlots, setContactSlots] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [googleAuthErrorOpen, setGoogleAuthErrorOpen] = useState(false);
  const [googleAuthErrorMessage, setGoogleAuthErrorMessage] = useState('');
  const [googleAuthLinking, setGoogleAuthLinking] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  const [nextStepErrors, setNextStepErrors] = useState({});
  /** User opened Google OAuth in another tab; poll status when they return so we can toast + allow booking. */
  const googleOAuthPendingRef = useRef(false);
  const googleStatusPollTimerRef = useRef(null);
  /** Avoid duplicate GET /subcategories for the same contact + category. */
  const subCategoriesFetchKeyRef = useRef('');
  const guestSearchTimerRef = useRef(null);

  const [guestSearchLoading, setGuestSearchLoading] = useState(false);
  const [guestSuggestions, setGuestSuggestions] = useState([]);

  /** Contact APIs can return duplicate rows; keep first occurrence only for dropdown. */
  const uniqueContacts = useMemo(() => {
    const list = Array.isArray(allContacts) ? allContacts : [];
    const seen = new Set();
    const out = [];
    for (const c of list) {
      const idPart =
        c?.id != null && c?.id !== '' ? `id:${String(c.id)}` : '';
      const fallbackPart = `fp:${String(c?.contactPerson || '').trim().toLowerCase()}|${String(c?.title || '').trim().toLowerCase()}|${String(c?.email || '').trim().toLowerCase()}|${String(c?.phone || '').trim().toLowerCase()}`;
      const key = idPart || fallbackPart;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(c);
    }
    return out;
  }, [allContacts]);

  const todayIso = useMemo(() => toLocalIsoDate(), []);
  const monthDateOptions = useMemo(() => {
    const base = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
    const ref = Number.isNaN(base.getTime()) ? new Date() : base;
    const year = ref.getFullYear();
    const month = ref.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = toLocalIsoDate();
    const bookingDate = (booking?.date || booking?.slotDate || '').split('T')[0];
    return Array.from({ length: daysInMonth }, (_, idx) => {
      const d = new Date(year, month, idx + 1);
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(idx + 1).padStart(2, '0')}`;
      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dateNum = String(idx + 1);
      const isPast = iso < todayStr;
      const isCurrentlySelected = iso === bookingDate;
      return { iso, dayName, dateNum, disabled: isPast && !isCurrentlySelected };
    }).filter(({ iso, disabled }) => {
      if (!booking?.id) return iso >= todayStr;
      // For edit: show the current booking date even if past, and all future dates
      return iso >= todayStr || iso === bookingDate;
    });
  }, [selectedDate, booking, todayIso]);

  const monthLabel = useMemo(() => {
    const base = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date();
    const ref = Number.isNaN(base.getTime()) ? new Date() : base;
    return ref.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [selectedDate]);

  useEffect(() => {
    subCategoriesFetchKeyRef.current = '';
  }, [form.contactId, open]);

  useEffect(() => {
    if (!open) {
      googleOAuthPendingRef.current = false;
      if (googleStatusPollTimerRef.current) {
        clearInterval(googleStatusPollTimerRef.current);
        googleStatusPollTimerRef.current = null;
      }
    }
  }, [open]);

  const checkGoogleCalendarStatusAfterOAuth = useCallback(async () => {
    if (!googleOAuthPendingRef.current || !open) return;
    try {
      const res = await googleAuthAPI.getStatus();
      const linked = parseGoogleCalendarLinkedFromStatusResponse(res?.data ?? res);
      if (linked) {
        googleOAuthPendingRef.current = false;
        if (googleStatusPollTimerRef.current) {
          clearInterval(googleStatusPollTimerRef.current);
          googleStatusPollTimerRef.current = null;
        }
        // Close the "Google Calendar Not Connected" popup immediately once status flips to connected.
        setGoogleAuthErrorOpen(false);
        setGoogleAuthErrorMessage('');
        toast.success('Google Calendar linked successfully. You can create your booking.');
      }
    } catch {
      // ignore transient errors while user completes OAuth
    }
  }, [open]);

  const handleGuestSearch = useCallback(async (val, immediate = false) => {
    if (!val || val.length < 1) {
      setGuestSuggestions([]);
      return;
    }

    const performSearch = async () => {
      setGuestSearchLoading(true);
      try {
        const res = await guestsAPI.getGuests(val, 1, 20);
        const data = res.data?.data || res.data || [];
        const items = Array.isArray(data) ? data : (data.items || []);
        setGuestSuggestions(items);
      } catch (err) {
        console.error('Guest search error:', err);
      } finally {
        setGuestSearchLoading(false);
      }
    };

    if (guestSearchTimerRef.current) clearTimeout(guestSearchTimerRef.current);

    if (immediate) {
      performSearch();
    } else {
      guestSearchTimerRef.current = setTimeout(performSearch, 500);
    }
  }, []);

  const handleSelectGuest = (guest) => {
    if (!guest) return;
    setForm((prev) => ({
      ...prev,
      firstName: guest.firstName || '',
      lastName: guest.lastName || '',
      email: guest.email || '',
      phone: guest.phone || '',
      gender: guest.gender?.toLowerCase() || '',
    }));
    setGuestSuggestions([]);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        void checkGoogleCalendarStatusAfterOAuth();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
    };
  }, [open, checkGoogleCalendarStatusAfterOAuth]);

  // GET /customer/contacts?page=1&limit=1000 — single load when dialog opens
  useEffect(() => {
    if (!open) return;
    contactsAPI
      .getContacts('', '', 0, 1000)
      .then((res) => {
        if (res?.data?.success) {
          setAllContacts(res.data.data || []);
        }
      })
      .catch((err) => console.error('Error fetching contacts:', err));
  }, [open]);

  useEffect(() => {
    if (!form.contactId) {
      setContactSlots([]);
      return;
    }
    setSlotsLoading(true);
    slotsAPI
      .getSlots({ contactId: form.contactId }, 0, 1000)
      .then((res) => {
        if (res?.data?.success) {
          let list = Array.isArray(res.data.data) ? res.data.data : [];
          if (selectedDate) {
            list = list.filter((s) => {
              const slotDate = String(s.date || '').split('T')[0];
              return slotDate === selectedDate;
            });
          }
          setContactSlots(list);
        } else {
          setContactSlots([]);
        }
      })
      .catch(() => setContactSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [form.contactId, selectedDate]);

  // Subcategories: contact row → options; if row is label-only but saved booking has numeric ids, GET by category so checks match.
  useEffect(() => {
    if (!open || !form.contactId) {
      setSubCategoryOptions([]);
      setSubCategoriesLoading(false);
      return undefined;
    }
    const contact = allContacts.find((c) => String(c.id) === String(form.contactId));
    if (!contact) {
      setSubCategoryOptions([]);
      return undefined;
    }

    const fromList = buildSubcategoryOptionsFromContactListRow(contact);
    const catId = getServiceCategoryIdFromContact(contact);
    const nameOnly = fromList.length > 0 && fromList.every((o) => o.kind === 'name');

    if (shouldFetchSubCategoryIdOptions(fromList, form.subCategorySelection, catId)) {
      const cacheKey = `${form.contactId}|${catId}`;
      if (subCategoriesFetchKeyRef.current === cacheKey) {
        setSubCategoriesLoading(false);
        return undefined;
      }
      let cancelled = false;
      setSubCategoriesLoading(true);
      fetchSubCategoryOptionsForCategory(catId, nameOnly, fromList)
        .then(({ rows, options }) => {
          if (cancelled) return;
          setSubCategoryOptions(options);
          if (rows.length > 0) {
            subCategoriesFetchKeyRef.current = cacheKey;
          }
        })
        .catch(() => {
          if (cancelled) setSubCategoryOptions(nameOnly ? fromList : []);
        })
        .finally(() => {
          if (!cancelled) setSubCategoriesLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (fromList.length > 0) {
      setSubCategoryOptions(fromList);
      setSubCategoriesLoading(false);
      return undefined;
    }

    if (!catId) {
      setSubCategoryOptions([]);
      setSubCategoriesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSubCategoriesLoading(true);
    fetchSubCategoryOptionsForCategory(catId, nameOnly, fromList)
      .then(({ options }) => {
        if (cancelled) return;
        setSubCategoryOptions(options);
      })
      .catch(() => {
        if (!cancelled) setSubCategoryOptions([]);
      })
      .finally(() => {
        if (!cancelled) setSubCategoriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, form.contactId, allContacts, form.subCategorySelection]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const run = async () => {
      if (!booking) {
        setForm(initialForm);
        setSelectedDate(todayIso);
        setCreateStep(0);
        return;
      }
      const bookingRecord = await fetchBookingDetailRecord(booking);
      if (cancelled) return;
      setForm(buildFormStateFromBooking(bookingRecord));
      setSelectedDate(resolveBookingSelectedDate(bookingRecord, todayIso));
      setCreateStep(0);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [booking, open, todayIso]);

  const handleChange = (key, value) => {
    let finalValue = value;
    if (key === 'phone') {
      finalValue = sanitizePhoneInput(value);
    }
    setForm((prev) => ({ ...prev, [key]: finalValue }));
    setNextStepErrors((prev) => {
      if (!prev[key]) return prev;
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleGoogleAuthLink = async () => {
    setGoogleAuthLinking(true);
    try {
      const res = await googleAuthAPI.getAuthUrl();
      const url = res?.data?.url || res?.data?.data?.url || res?.data?.payload?.url || res?.data?.authUrl || (typeof res?.data === 'string' && res.data.startsWith('http') ? res.data : null);
      if (url) {
        googleOAuthPendingRef.current = true;
        window.open(url, '_blank');
        if (googleStatusPollTimerRef.current) clearInterval(googleStatusPollTimerRef.current);
        let ticks = 0;
        googleStatusPollTimerRef.current = setInterval(() => {
          ticks += 1;
          void checkGoogleCalendarStatusAfterOAuth();
          if (ticks >= 120 || !googleOAuthPendingRef.current) {
            if (googleStatusPollTimerRef.current) {
              clearInterval(googleStatusPollTimerRef.current);
              googleStatusPollTimerRef.current = null;
            }
          }
        }, 3000);
      } else {
        alert('Failed to retrieve Google Auth URL');
      }
    } catch (error) {
      alert('Failed to retrieve Google Auth URL');
      console.error(error);
    } finally {
      setGoogleAuthLinking(false);
    }
  };

  const handleSubmit = async () => {
    const sel = Array.isArray(form.subCategorySelection) ? form.subCategorySelection : [];
    const { ids: subIds, names: subNames } = splitSubCategoryPayload(sel, subCategoryOptions);
    const hasSelection = subIds.length > 0 || subNames.length > 0;
    if (subCategoryOptions.length > 0 && !hasSelection) {
      alert(t('bookingForm.subCategoriesRequired'));
      return;
    }

    /** Name-only UI values → ids via category list (POST/PUT send ids only, never name fields). */
    let idsFromResolvedNames = [];
    if (subNames.length > 0) {
      const contact = allContacts.find((c) => String(c.id) === String(form.contactId));
      const catId = contact ? getServiceCategoryIdFromContact(contact) : null;
      if (!catId) {
        alert(t('bookingForm.subCategoryResolveNeedsCategory'));
        return;
      }
      try {
        const res = await subCategoriesAPI.getSubCategories(catId);
        const list = parseSubCategoriesListResponse(res);
        const unresolved = [];
        for (const name of subNames) {
          const id = matchSubcategoryIdByLabel(list, name);
          if (id != null) idsFromResolvedNames.push(id);
          else unresolved.push(name);
        }
        if (unresolved.length > 0) {
          alert(t('bookingForm.subCategoryResolveFailed'));
          return;
        }
      } catch (e) {
        console.error(e);
        alert(t('bookingForm.errorSave'));
        return;
      }
    }

    /** POST/PUT: only `subCategoryIds` as JSON numbers, e.g. [7, 24] — no subCategories / subCategoryNames */
    const subCategoryIdsPayload = [];
    const seenId = new Set();
    for (const raw of [...subIds, ...idsFromResolvedNames]) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0 && !seenId.has(n)) {
        seenId.add(n);
        subCategoryIdsPayload.push(n);
      }
    }

    if (subCategoryOptions.length > 0 && hasSelection && subCategoryIdsPayload.length === 0) {
      alert(t('bookingForm.subCategoriesRequired'));
      return;
    }
    const emailTrimmed = String(form.email || '').trim();
    if (!emailTrimmed) {
      setNextStepErrors((prev) => ({ ...prev, email: 'Please enter email.' }));
      if (isCreateFlow) setCreateStep(0);
      return;
    }
    if (!isValidEmail(emailTrimmed)) {
      setNextStepErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
      if (isCreateFlow) setCreateStep(0);
      return;
    }
    const phoneTrimmed = String(form.phone || '').trim();
    if (!phoneTrimmed) {
      setNextStepErrors((prev) => ({ ...prev, phone: 'Please enter phone number.' }));
      if (isCreateFlow) setCreateStep(0);
      return;
    }
    if (!isValidPhoneNumber(phoneTrimmed)) {
      setNextStepErrors((prev) => ({ ...prev, phone: 'Please enter a valid phone number (6-15 digits).' }));
      if (isCreateFlow) setCreateStep(0);
      return;
    }

    if (!String(form.slotId || '').trim()) {
      alert('Please select a slot.');
      return;
    }
    const selectedSlotForValidation = contactSlots.find((s) => String(s.id) === String(form.slotId));
    const isKeepingExistingSlot =
      booking?.id && String(booking.slotId || '') === String(form.slotId);
    if (
      selectedSlotForValidation &&
      !isKeepingExistingSlot &&
      !isSlotBookableFromUserSlotsApi(selectedSlotForValidation)
    ) {
      alert('Please select an available slot.');
      return;
    }

    setSubmitting(true);
    try {
      const selectedSlot = contactSlots.find((s) => String(s.id) === String(form.slotId));
      const resolvedContactId = form.contactId || selectedSlot?.contactId || '';
      const normalizedContactId = Number(resolvedContactId);
      const guestPayload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        gender: form.gender,
      };
      const slotDateVal = selectedSlot?.date ? String(selectedSlot.date).split('T')[0] : selectedDate;
      const rawSlotId = form.slotId;
      const numSlotId = Number(rawSlotId);
      const parsedSlotId = Number.isFinite(numSlotId) && numSlotId > 0 ? numSlotId : String(rawSlotId ?? '').trim();

      const payload = {
        title: form.title,
        slotId: parsedSlotId,
        newSlotId: parsedSlotId,
        SlotId: parsedSlotId,
        slot_id: parsedSlotId,
        ...(slotDateVal ? { date: slotDateVal, slotDate: slotDateVal, appointmentDate: slotDateVal } : {}),
        ...(!booking?.id ? { status: DEFAULT_NEW_BOOKING_STATUS } : {}),
        ...(Number.isFinite(normalizedContactId) && normalizedContactId > 0 ? { contactId: normalizedContactId } : {}),
        ...(subCategoryIdsPayload.length > 0 ? { subCategoryIds: subCategoryIdsPayload } : {}),
        guest: guestPayload,
        serviceAddress: form.serviceAddress,
        notes: form.notes,
        numberOfGuests: Number.parseInt(form.numberOfGuests || 1, 10),
        customerGender: form.gender,
        customerFirstName: form.firstName,
        customerLastName: form.lastName,
        meetingType: form.meetingType,
      };

      if (booking?.id) {
        await bookingsAPI.updateBooking(booking.id, payload);
        const isSlotChanged = booking.slotId && String(booking.slotId) !== String(form.slotId);
        if (isSlotChanged) {
          try {
            await bookingsAPI.rescheduleBooking(booking.id, {
              newSlotId: parsedSlotId,
              slotId: parsedSlotId,
              reason: 'Slot updated during booking edit',
            });
          } catch (rescheduleErr) {
            console.warn('Reschedule API call during edit:', rescheduleErr);
          }
        }
      } else {
        await bookingsAPI.createBooking(payload);
      }
      if (onSuccess) onSuccess();
      onClose();
      setForm(initialForm);
      setSubCategoryOptions([]);
    } catch (error) {
      const apiCode = error?.response?.data?.error?.code || error?.response?.data?.code;
      if (apiCode === 'GOOGLE_CALENDAR_NOT_CONNECTED') {
        setGoogleAuthErrorMessage('Please first connect your Google Calendar.');
        setGoogleAuthErrorOpen(true);
      } else {
        const apiMsg = getBookingApiUserMessage(error);
        const msg = apiMsg || t('bookingForm.errorSave');
        toast.error(msg, {
          autoClose: msg.length > 140 ? 12000 : 6000,
          style: { whiteSpace: 'pre-line' },
        });
        console.error(error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      googleOAuthPendingRef.current = false;
      if (googleStatusPollTimerRef.current) {
        clearInterval(googleStatusPollTimerRef.current);
        googleStatusPollTimerRef.current = null;
      }
      setCalendarOpen(false);
      onClose();
      setForm(initialForm);
      setNextStepErrors({});
      setSubCategoryOptions([]);
      setSelectedDate(todayIso);
      setCreateStep(0);
    }
  };

  const handleNextStep = () => {
    const errors = {};
    if (!String(form.contactId || '').trim()) errors.contactId = t('bookingForm.errorSelectService');
    if (!String(form.title || '').trim()) errors.title = 'Please enter title.';
    else if (String(form.title).trim().length > 250) errors.title = 'Title cannot exceed 250 characters.';
    if (!String(form.firstName || '').trim()) errors.firstName = 'Please enter first name.';
    if (!String(form.lastName || '').trim()) errors.lastName = 'Please enter last name.';
    const emailTrimmed = String(form.email || '').trim();
    if (!emailTrimmed) {
      errors.email = 'Please enter email.';
    } else if (!isValidEmail(emailTrimmed)) {
      errors.email = 'Please enter a valid email address.';
    }

    const phoneTrimmed = String(form.phone || '').trim();
    if (!phoneTrimmed) {
      errors.phone = 'Please enter phone number.';
    } else if (!isValidPhoneNumber(phoneTrimmed)) {
      errors.phone = 'Please enter a valid phone number (6-15 digits).';
    }
    if (!Array.isArray(form.subCategorySelection) || form.subCategorySelection.length === 0) {
      errors.subCategorySelection = 'Please select sub category.';
    }
    if (!String(form.gender || '').trim()) errors.gender = 'Please select gender.';
    if (!String(form.numberOfGuests || '').trim() || Number(form.numberOfGuests) <= 0) {
      errors.numberOfGuests = 'Please enter number of guests.';
    }
    if (!String(form.meetingType || '').trim()) errors.meetingType = 'Please select meeting type.';
    if (String(form.meetingType || '').toLowerCase() === 'offline' && !String(form.serviceAddress || '').trim()) {
      errors.serviceAddress = 'Please enter service address for offline booking.';
    }
    setNextStepErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setCreateStep(1);
  };

  const isCreateFlow = !booking?.id;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            '& .MuiFormLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
            '& .MuiInputLabel-asterisk': {
              color: '#d32f2f !important',
              fontWeight: 700,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.25,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #f7f8fa 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {getBookingFormDialogTitle(booking, createStep, t)}
        </DialogTitle>
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: '20px !important' },
            pb: 3,
            backgroundColor: '#fcfcfd',
          }}
        >
          <Grid container spacing={2}>
            {(isCreateFlow ? createStep === 0 : true) && (
              <>
                {/* ── Booking Info card ── */}
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2.5, borderRadius: 3,
                    background: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: 'none',
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FF8C42', mb: 2, letterSpacing: 0.3 }}>
                      Booking Info
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth size="small" required sx={{ bgcolor: '#fff' }} error={Boolean(nextStepErrors.contactId)}>
                          <InputLabel>{t('bookingForm.contactSelect')}</InputLabel>
                          <Select
                            label={t('bookingForm.contactSelect')}
                            value={form.contactId}
                            onChange={(e) => {
                              handleChange('contactId', e.target.value);
                              handleChange('slotId', '');
                              handleChange('subCategorySelection', []);
                            }}
                          >
                            {uniqueContacts.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.title ? `${c.contactPerson || '—'} — ${c.title}` : c.contactPerson || t('bookingForm.contactLabel', { id: c.id })}
                              </MenuItem>
                            ))}
                          </Select>
                          {nextStepErrors.contactId && <FormHelperText>{nextStepErrors.contactId}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          label={t('bookingForm.title')}
                          fullWidth size="small"
                          value={form.title}
                          onChange={(e) => handleChange('title', e.target.value.slice(0, 250))}
                          inputProps={{ maxLength: 250 }}
                          required variant="outlined"
                          error={Boolean(nextStepErrors.title)}
                          helperText={nextStepErrors.title || ''}
                          sx={{ bgcolor: '#fff' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth required size="small" sx={{ bgcolor: '#fff' }} error={Boolean(nextStepErrors.meetingType)}>
                          <InputLabel>Meeting Type</InputLabel>
                          <Select label="Meeting Type" value={form.meetingType || 'offline'} onChange={(e) => handleChange('meetingType', e.target.value)}>
                            <MenuItem value="online">Online</MenuItem>
                            <MenuItem value="offline">Offline</MenuItem>
                          </Select>
                          {nextStepErrors.meetingType && <FormHelperText>{nextStepErrors.meetingType}</FormHelperText>}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth required size="small" sx={{ bgcolor: '#fff' }} disabled={form.contactId ? subCategoriesLoading : true} error={Boolean(nextStepErrors.subCategorySelection)}>
                          <InputLabel id="booking-subcat-multiple-label">{t('bookingForm.serviceSubcategories')}</InputLabel>
                          <Select
                            labelId="booking-subcat-multiple-label"
                            multiple
                            value={Array.isArray(form.subCategorySelection) ? form.subCategorySelection : []}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForm((prev) => ({ ...prev, subCategorySelection: normalizeSubCategorySelectValue(v) }));
                            }}
                            input={<OutlinedInput label={t('bookingForm.serviceSubcategories')} />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.25 }}>
                                {selected.map((val) => {
                                  const label =
                                    subCategoryOptions.find(
                                      (o) =>
                                        o.value === val ||
                                        (typeof o.value === 'number' && Number(val) === o.value) ||
                                        String(o.value) === String(val)
                                    )?.label || String(val);
                                  return (
                                    <Chip
                                      key={`subcat-${String(val)}`}
                                      label={label}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.72rem',
                                        borderRadius: '999px',
                                        fontWeight: 700,
                                        bgcolor: 'rgba(33, 150, 243, 0.12)',
                                        color: '#1976d2',
                                        border: '1px solid rgba(33, 150, 243, 0.35)',
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {subCategoryOptions.map((row) => (
                              <MenuItem key={`${row.kind}-${String(row.value)}`} value={row.value}>
                                <Checkbox checked={selectionIncludesValue(form.subCategorySelection, row.value)} size="small" />
                                <ListItemText primary={row.label} />
                              </MenuItem>
                            ))}
                          </Select>
                          {nextStepErrors.subCategorySelection ? (
                            <FormHelperText>{nextStepErrors.subCategorySelection}</FormHelperText>
                          ) : (
                            form.contactId && !subCategoriesLoading && subCategoryOptions.length === 0 && <FormHelperText>{t('bookingForm.noSubcategoriesForContact')}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      {String(form.meetingType || '').toLowerCase() === 'offline' && (
                        <Grid item xs={12}>
                          <TextField
                            label={t('bookingForm.serviceAddress')}
                            fullWidth
                            size="small"
                            required
                            value={form.serviceAddress}
                            onChange={(e) => handleChange('serviceAddress', e.target.value)}
                            error={Boolean(nextStepErrors.serviceAddress)}
                            helperText={nextStepErrors.serviceAddress || ''}
                            sx={{ bgcolor: '#fff' }}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>

                {/* ── Guest Details card ── */}
                <Grid item xs={12}>
                  <Box sx={{
                    p: 2.5, borderRadius: 3,
                    background: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, letterSpacing: 0.3 }}>
                      {t('bookingForm.guestDetails')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          freeSolo
                          disableClearable
                          options={guestSuggestions}
                          loading={guestSearchLoading}
                          value={form.firstName ?? ''}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            if (!option) return '';
                            return `${option.firstName || ''} ${option.lastName || ''}`.trim();
                          }}
                          onInputChange={(event, newInputValue, reason) => {
                            // Without skipping `reset`, MUI can clear the field when `value` syncs from props (e.g. after edit load).
                            if (reason === 'reset') return;
                            handleChange('firstName', newInputValue);
                            handleGuestSearch(newInputValue, false);
                          }}
                          onChange={(event, newValue) => {
                            if (typeof newValue === 'string') {
                              handleChange('firstName', newValue);
                              setGuestSuggestions([]);
                            } else if (newValue && typeof newValue === 'object') {
                              handleSelectGuest(newValue);
                            }
                          }}
                          renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.firstName} {option.lastName}
                              </Typography>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('bookingForm.guestFirstName')}
                              fullWidth size="small"
                              required
                              error={Boolean(nextStepErrors.firstName)}
                              helperText={nextStepErrors.firstName || ''}
                              sx={{ bgcolor: '#fff' }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {guestSearchLoading ? (
                                      <CircularProgress color="inherit" size={20} />
                                    ) : (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleGuestSearch(form.firstName, true)}
                                        edge="end"
                                        title="Search Guest"
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                    {params.InputProps.endAdornment}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label={t('bookingForm.guestLastName')} fullWidth size="small" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)} required error={Boolean(nextStepErrors.lastName)} helperText={nextStepErrors.lastName || ''} sx={{ bgcolor: '#fff' }} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t('bookingForm.guestEmail')}
                          fullWidth
                          size="small"
                          value={form.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          onBlur={() => {
                            const trimmed = String(form.email || '').trim();
                            if (!trimmed) {
                              setNextStepErrors((prev) => ({ ...prev, email: 'Please enter email.' }));
                            } else if (!isValidEmail(trimmed)) {
                              setNextStepErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
                            }
                          }}
                          type="email"
                          required
                          error={Boolean(nextStepErrors.email)}
                          helperText={nextStepErrors.email || ''}
                          sx={{ bgcolor: '#fff' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label={t('bookingForm.guestPhone')}
                          fullWidth
                          size="small"
                          value={form.phone}
                          onChange={(e) => {
                            const clean = sanitizePhoneInput(e.target.value);
                            handleChange('phone', clean);
                          }}
                          onBlur={() => {
                            const trimmed = String(form.phone || '').trim();
                            if (!trimmed) {
                              setNextStepErrors((prev) => ({ ...prev, phone: 'Please enter phone number.' }));
                            } else if (!isValidPhoneNumber(trimmed)) {
                              setNextStepErrors((prev) => ({ ...prev, phone: 'Please enter a valid phone number (6-15 digits).' }));
                            }
                          }}
                          inputProps={{ inputMode: 'tel', maxLength: 20 }}
                          required
                          error={Boolean(nextStepErrors.phone)}
                          helperText={nextStepErrors.phone || ''}
                          sx={{ bgcolor: '#fff' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth required size="small" sx={{ bgcolor: '#fff' }} error={Boolean(nextStepErrors.gender)}>
                          <InputLabel>{t('bookingForm.gender')}</InputLabel>
                          <Select label={t('bookingForm.gender')} value={form.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                          {nextStepErrors.gender && <FormHelperText>{nextStepErrors.gender}</FormHelperText>}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField label={t('bookingForm.numberOfGuests')} fullWidth required size="small" type="number" inputProps={{ min: 1 }} value={form.numberOfGuests} onChange={(e) => handleChange('numberOfGuests', e.target.value)} error={Boolean(nextStepErrors.numberOfGuests)} helperText={nextStepErrors.numberOfGuests || ''} sx={{ bgcolor: '#fff' }} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label={t('bookingForm.notes')}
                          fullWidth size="small"
                          multiline minRows={3}
                          value={form.notes}
                          onChange={(e) => handleChange('notes', e.target.value)}
                          sx={{ bgcolor: '#fff' }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </>
            )}

            {(isCreateFlow ? createStep === 1 : true) && (
              <>
                {/* ── Date Picker Section ── */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background: '#fff',
                      border: '1px solid rgba(0,0,0,0.08)',
                      boxShadow: 'none',
                    }}
                  >
                    {/* Header row: label + calendar icon button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, color: '#FF8C42', letterSpacing: 0.3 }}>
                        Select Date &amp; Slot
                        <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>
                          *
                        </Typography>
                      </Typography>
                      <Box
                        component="button"
                        onClick={() => setCalendarOpen(true)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          px: 2, py: 1,
                          borderRadius: 2,
                          border: '1.5px solid #FF8C42',
                          background: '#fff',
                          color: '#FF8C42',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            background: '#FF8C42',
                            color: '#fff',
                            borderColor: '#FF8C42',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                        Open Calendar
                      </Box>
                    </Box>

                    {/* Selected date display */}
                    {selectedDate && (
                      <Box
                        sx={{
                          mb: 2, px: 2.5, py: 1.5,
                          borderRadius: 2,
                          background: '#FF8C42',
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          boxShadow: 'none',
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 18, color: '#fff' }} />
                        <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem' }}>
                          {formatSelectedDateLongLabel(selectedDate)}
                        </Typography>
                      </Box>
                    )}

                    {/* Month label + full-month horizontal date strip */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#555', letterSpacing: 0.3 }}>
                        {monthLabel}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        overflowX: 'auto',
                        pb: 1,
                        pt: 1,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-track': { background: '#f0f0f0', borderRadius: 2 },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(255, 140, 66, 0.4)', borderRadius: 2 },
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, minWidth: 'max-content' }}>
                        {monthDateOptions.map((d) => {
                          const isSelected = selectedDate === d.iso;
                          return (
                            <Box
                              key={d.iso}
                              onClick={() => {
                                if (d.disabled) return;
                                setSelectedDate(d.iso);
                                handleChange('slotId', '');
                              }}
                              sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                px: 1.5, py: 1.5,
                                borderRadius: 2.5,
                                minWidth: 52,
                                cursor: d.disabled ? 'not-allowed' : 'pointer',
                                border: isSelected ? '2px solid #FF8C42' : '1.5px solid #e8e8e8',
                                opacity: d.disabled ? 0.5 : 1,
                                background: isSelected
                                  ? '#FF8C42'
                                  : '#fff',
                                boxShadow: 'none',
                                transition: 'all 0.18s ease',
                                '&:hover': {
                                  borderColor: d.disabled ? '#e8e8e8' : '#FF8C42',
                                  transform: d.disabled ? 'none' : 'translateY(-2px)',
                                  boxShadow: 'none',
                                },
                              }}
                            >
                              <Typography sx={{
                                fontSize: '0.65rem', fontWeight: 600,
                                color: isSelected ? 'rgba(255,255,255,0.85)' : '#999',
                                textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.4,
                              }}>
                                {d.dayName}
                              </Typography>
                              <Typography sx={{
                                fontSize: '1.1rem', fontWeight: 800, lineHeight: 1,
                                color: isSelected ? '#fff' : '#1a1a1a',
                              }}>
                                {d.dateNum}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                {/* ── Slots Section ── */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      background: '#fff',
                      border: '1px solid rgba(0,0,0,0.08)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, color: 'text.primary', mb: 2, letterSpacing: 0.3 }}>
                      Available Slots
                      <Typography component="span" sx={{ color: 'error.main', ml: 0.25 }} aria-hidden>
                        *
                      </Typography>
                    </Typography>
                    <AvailableSlotsPanel
                      contactId={form.contactId}
                      slotsLoading={slotsLoading}
                      contactSlots={contactSlots}
                      booking={booking}
                      formSlotId={form.slotId}
                      translate={t}
                      onSelectSlot={(slotId) => handleChange('slotId', slotId)}
                    />
                  </Box>
                </Grid>

                {/* ── Custom Calendar Modal ── */}
                {calendarOpen && (
                  <Box
                    onClick={() => setCalendarOpen(false)}
                    sx={{
                      position: 'fixed', inset: 0, zIndex: 1400,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.45)',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        bgcolor: '#fff',
                        borderRadius: 4,
                        boxShadow: '0 24px 80px rgba(0,0,0,0.22)',
                        p: 3,
                        width: { xs: '90vw', sm: 360 },
                        maxHeight: '90vh',
                        overflow: 'auto',
                      }}
                    >
                      <MiniCalendar
                        value={selectedDate}
                        originalValue={(booking?.date || booking?.slotDate || '').split('T')[0]}
                        minDate={todayIso}
                        onChange={(iso) => {
                          setSelectedDate(iso);
                          handleChange('slotId', '');
                          setCalendarOpen(false);
                        }}
                        onClose={() => setCalendarOpen(false)}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}


          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fff',
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-end',
            gap: 1.5,
          }}
        >
          <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('common.cancel')}
          </Button>
          {isCreateFlow && createStep === 1 && (
            <Button onClick={() => setCreateStep(0)} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Previous
            </Button>
          )}
          <Button
            variant="contained"
            onClick={isCreateFlow && createStep === 0 ? handleNextStep : handleSubmit}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              borderRadius: 2,
              backgroundColor: '#FF8C42',
              '&:hover': { backgroundColor: '#E06526' },
            }}
          >
            {getBookingFormPrimaryActionLabel(isCreateFlow, createStep, booking, t)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={googleAuthErrorOpen} onClose={() => setGoogleAuthErrorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #eee' }}>Google Calendar Not Connected</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>{googleAuthErrorMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGoogleAuthErrorOpen(false)} disabled={googleAuthLinking}>Cancel</Button>
          <Button variant="contained" onClick={handleGoogleAuthLink} disabled={googleAuthLinking} sx={{ bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367d6' } }}>
            Link Google Calendar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

BookingForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  booking: PropTypes.object,
};
